// src/components/common/Layout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  AppBar,
  Toolbar,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  Settings as SettingsIcon,
  Logout,
  Brightness4,
  Brightness7,
  KeyboardArrowDown,
  Help,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useThemeContext } from '../../context/ThemeContext';
import { vendorApi } from '../../api/vendorApi';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { resolveImageUrl, getUserAvatarUrl } from '../../utils/imageUtils';

const Layout = () => {
  const { user, logout } = useAuth();
  const { siteName, siteLogo } = useSettings();
  const { colors, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const isDark = colors?.themeMode === 'dark';
  const primaryColor = colors?.primaryColor || '#E31E24';

  const userAvatarUrl = getUserAvatarUrl(user);
  const siteLogoUrl = resolveImageUrl(siteLogo);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 15);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        if (user?.role === 'vendor') {
          const response = await vendorApi.getUnreadCount();
          setUnreadCount(response?.unreadCount || 0);
        }
      } catch (err) {
        setUnreadCount(0);
      }
    };
    fetchUnreadCount();
  }, [user]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotifOpen = (e) => setNotifAnchorEl(e.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleMessagesClick = () => {
    navigate(user?.role === 'admin' ? '/admin/messages' : '/vendor/chat');
  };

  const handleSupportClick = () => {
    navigate(user?.role === 'admin' ? '/admin/support' : '/vendor/support');
    handleMenuClose();
  };

  const handleSettingsClick = () => {
    navigate(user?.role === 'admin' ? '/admin/settings' : '/vendor/settings');
    handleMenuClose();
  };

  const handleNotificationClick = () => {
    handleMessagesClick();
    handleNotifClose();
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/vendor/dashboard') return 'Dashboard';
    if (path.includes('/support')) return 'Support Center';
    if (path.includes('/chat') || path.includes('/messages')) return 'Messages';
    if (path.includes('/settings')) return 'Settings';

    const segments = path.split('/').filter(Boolean);
    if (segments.length > 1) {
      return segments[1].charAt(0).toUpperCase() + segments[1].slice(1).replace('-', ' ');
    }
    return siteName || 'Dashboard';
  };

  const getAvatarLetter = () => {
    if (user?.name) return user.name[0].toUpperCase();
    if (user?.storeName) return user.storeName[0].toUpperCase();
    return 'U';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={scrolled ? 3 : 0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: scrolled
            ? isDark ? 'rgba(18, 18, 30, 0.92)' : 'rgba(255, 255, 255, 0.92)'
            : 'background.paper',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: '1px solid',
          borderColor: scrolled ? 'divider' : 'transparent',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 }, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {siteLogoUrl ? (
              <Box
                sx={{
                  width: { xs: 30, sm: 34 },
                  height: { xs: 30, sm: 34 },
                  mr: 1.5,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={siteLogoUrl}
                  alt={siteName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: { xs: 30, sm: 34 },
                  height: { xs: 30, sm: 34 },
                  bgcolor: primaryColor,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 900 }}>
                  {siteName ? siteName.charAt(0) : 'V'}
                </Typography>
              </Box>
            )}

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {getPageTitle()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'}>
              <IconButton onClick={toggleTheme}>
                {isDark ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Help & Support">
              <IconButton onClick={handleSupportClick}>
                <Help fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications">
              <IconButton color="inherit" onClick={handleNotifOpen}>
                <Badge badgeContent={unreadCount} color="error">
                  <Notifications fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton onClick={handleMenuOpen} sx={{ p: 0.5, ml: 0.5 }}>
              <Avatar
                src={userAvatarUrl}
                sx={{
                  bgcolor: userAvatarUrl ? 'transparent' : primaryColor,
                  width: { xs: 30, sm: 34 },
                  height: { xs: 30, sm: 34 },
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 700,
                  border: userAvatarUrl ? '2px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                {!userAvatarUrl && getAvatarLetter()}
              </Avatar>
            </IconButton>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                cursor: 'pointer',
                p: 0.5,
              }}
              onClick={handleMenuOpen}
            >
              <Typography variant="body2" fontWeight="700" sx={{ mr: 0.5 }}>
                {user?.name || user?.storeName || 'User'}
              </Typography>
              <KeyboardArrowDown sx={{ fontSize: 18, opacity: 0.7 }} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={handleNotifClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: { xs: 290, sm: 340 },
            maxHeight: 380,
            borderRadius: 3,
            mt: 1.2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="800">
            Notifications
          </Typography>
        </Box>
        <MenuItem onClick={handleNotificationClick} sx={{ py: 1.8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
            <Badge badgeContent={unreadCount} color="error" variant="dot">
              <Notifications color="action" />
            </Badge>
            <Box>
              <Typography variant="body2" fontWeight="600">
                {unreadCount > 0 ? `${unreadCount} unread message(s)` : 'No unread messages'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to open chat portal
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: { xs: 210, sm: 240 },
            borderRadius: 3,
            mt: 1.2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.8, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={userAvatarUrl}
              sx={{
                width: 36,
                height: 36,
                bgcolor: userAvatarUrl ? 'transparent' : primaryColor,
                border: userAvatarUrl ? '2px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              {!userAvatarUrl && getAvatarLetter()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="800">
                {user?.name || user?.storeName || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(user?.role === 'admin' ? '/admin/profile' : '/vendor/profile');
          }}
          sx={{ py: 1.2, borderRadius: 1.5, mx: 0.8, my: 0.3 }}
        >
          <Person fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          Profile
        </MenuItem>

        <MenuItem
          onClick={handleSettingsClick}
          sx={{ py: 1.2, borderRadius: 1.5, mx: 0.8, my: 0.3 }}
        >
          <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          Settings
        </MenuItem>

        <Divider sx={{ my: 0.8 }} />

        <MenuItem
          onClick={logout}
          sx={{
            py: 1.2,
            borderRadius: 1.5,
            mx: 0.8,
            my: 0.3,
            color: 'error.main',
            '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
          }}
        >
          <Logout fontSize="small" sx={{ mr: 1.5 }} />
          Logout
        </MenuItem>
      </Menu>

      <Box sx={{ display: 'flex', flex: 1, mt: { xs: '56px', sm: '64px' }, minHeight: 'calc(100vh - 120px)' }}>
        <Sidebar mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} userAvatarUrl={userAvatarUrl} />
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2.5, md: 3, lg: 4 },
            width: { sm: `calc(100% - 260px)` },
            minHeight: 'calc(100vh - 120px)',
            bgcolor: 'background.default',
            transition: 'padding 0.3s ease',
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: 260 },
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          transition: 'left 0.3s ease',
        }}
      >
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;